/**
 * Tour Utilities
 *
 * Manages tour tracking for first-time displays across the app.
 *
 * Storage Strategy (Hybrid Approach):
 * - ALL accounts: Primary storage is cookies (fast, synchronous, no shared state)
 * - Authenticated accounts: Additionally sync to Supabase as backup (cross-device support)
 * - Free accounts: Cookies only (no DB sync due to shared UID)
 *
 * On authenticated user login, DB preferences are loaded into cookies via AuthContext.
 * When marking tours as shown, authenticated users update both cookie + DB.
 *
 * Tour Types:
 * - input: Message input page (analyzer step 1)
 * - results: Analysis results page (analyzer step 2)
 * - response: Response draft page (analyzer step 3)
 * - high-risk: High-risk message flow
 */

import { getCookie, setCookie, deleteCookie } from './storage'
import { getUserPreferences, updateUserPreferences } from './account/userPreferences'
import { supabase } from './supabase'

const TOUR_STORAGE_KEY = 'lina_tours_shown'

export interface ToursShown {
  input: boolean
  results: boolean
  response: boolean
  'high-risk': boolean
  'lens-input': boolean
  'lens-results': boolean
}

// Analyzer tours sync to user preferences. Lens tours are cookie-only until
// those columns exist.
const TOUR_DB_MAP: Partial<Record<keyof ToursShown, string>> = {
  input: 'analyzer_step1_tour_shown',
  results: 'analyzer_step2_tour_shown',
  response: 'analyzer_step3_tour_shown',
  'high-risk': 'high_risk_tour_shown',
}

/**
 * Check if user is authenticated (not a free account)
 * Note: This checks for actual auth user, not account type
 */
async function isAuthenticatedUser(): Promise<boolean> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) return false

    // Additional check: free accounts have a specific user_id pattern
    // But for tour purposes, we just check if they're logged in
    return true
  } catch {
    return false
  }
}

/**
 * Get tour shown status from cookies
 * This is the PRIMARY source for all users (free and authenticated)
 * Falls back to localStorage for backward compatibility during migration
 */
function getTourStatusFromCookies(): ToursShown {
  try {
    // Try cookie first (new system)
    const cookieStored = getCookie(TOUR_STORAGE_KEY)
    if (cookieStored) {
      return JSON.parse(cookieStored)
    }

    // Fall back to localStorage (legacy)
    const localStored = localStorage.getItem(TOUR_STORAGE_KEY)
    if (localStored) {
      // Migrate to cookie
      const parsed = JSON.parse(localStored)
      setCookie(TOUR_STORAGE_KEY, localStored, 3650) // 10 years
      localStorage.removeItem(TOUR_STORAGE_KEY)
      return parsed
    }
  } catch (err) {
    console.error('[Tour] Failed to read tour status from cookies:', err)
  }

  return {
    input: false,
    results: false,
    response: false,
    'high-risk': false,
    'lens-input': false,
    'lens-results': false,
  }
}

/**
 * Save tour status to cookies
 * This is the PRIMARY storage method for all users
 */
function saveTourStatusToCookies(status: ToursShown): void {
  try {
    const serialized = JSON.stringify(status)
    setCookie(TOUR_STORAGE_KEY, serialized, 3650) // 10 years - effectively permanent
    // Also remove from localStorage if it exists (cleanup)
    localStorage.removeItem(TOUR_STORAGE_KEY)
  } catch (err) {
    console.error('[Tour] Failed to save tour status to cookies:', err)
  }
}

/**
 * Sync tour preferences from database to cookies
 * Called on login for authenticated users to restore cross-device state
 */
export async function syncTourPreferencesFromDatabase(): Promise<void> {
  try {
    const isAuth = await isAuthenticatedUser()
    if (!isAuth) {
      console.log('[Tour] Skipping DB sync - not authenticated')
      return
    }

    const prefs = await getUserPreferences()
    if (!prefs) {
      console.log('[Tour] No user preferences found - using defaults')
      return
    }

    // Load DB values into cookies
    const current = getTourStatusFromCookies()
    const toursShown: ToursShown = {
      input: prefs.analyzer_step1_tour_shown ?? false,
      results: prefs.analyzer_step2_tour_shown ?? false,
      response: prefs.analyzer_step3_tour_shown ?? false,
      'high-risk': prefs.high_risk_tour_shown ?? false,
      'lens-input': current['lens-input'] ?? false,
      'lens-results': current['lens-results'] ?? false,
    }

    saveTourStatusToCookies(toursShown)
    console.log('[Tour] Synced tour preferences from database to cookies:', toursShown)
  } catch (err) {
    console.error('[Tour] Failed to sync tour preferences from database:', err)
  }
}

/**
 * Get tour shown status (always from cookies - synchronous and fast)
 */
export function getTourStatus(): ToursShown {
  return getTourStatusFromCookies()
}

/**
 * Check if a specific tour has been shown (synchronous)
 */
export function hasTourBeenShown(tour: keyof ToursShown): boolean {
  const status = getTourStatusFromCookies()
  return status[tour] === true
}

/**
 * Mark a specific tour as shown
 * - Updates cookies immediately (all users)
 * - Syncs to database in background (authenticated users only)
 */
export async function markTourAsShown(tour: keyof ToursShown): Promise<void> {
  // Update cookies immediately (primary storage)
  const status = getTourStatusFromCookies()
  status[tour] = true
  saveTourStatusToCookies(status)
  console.log(`[Tour] Marked ${tour} tour as shown in cookies`)

  // Sync to database in background (authenticated users only)
  const isAuth = await isAuthenticatedUser()
  const dbColumn = TOUR_DB_MAP[tour]
  if (isAuth && dbColumn) {
    try {
      await updateUserPreferences({ [dbColumn]: true } as any)
      console.log(`[Tour] Synced ${tour} tour to database`)
    } catch (err) {
      console.error(`[Tour] Failed to sync ${tour} tour to database:`, err)
      // Non-fatal - cookie is already updated
    }
  }
}

/**
 * Reset all tours (for testing or admin purposes)
 * - Clears cookies (all users)
 * - Resets database (authenticated users only)
 */
export async function resetAllTours(): Promise<void> {
  // Clear cookies immediately
  try {
    deleteCookie(TOUR_STORAGE_KEY)
    localStorage.removeItem(TOUR_STORAGE_KEY)
    console.log('[Tour] Reset all tours in cookies')
  } catch (err) {
    console.error('[Tour] Failed to reset tours in cookies:', err)
  }

  // Reset database (authenticated users only)
  const isAuth = await isAuthenticatedUser()
  if (isAuth) {
    try {
      await updateUserPreferences({
        analyzer_step1_tour_shown: false,
        analyzer_step2_tour_shown: false,
        analyzer_step3_tour_shown: false,
        high_risk_tour_shown: false,
      })
      console.log('[Tour] Reset all tours in database')
    } catch (err) {
      console.error('[Tour] Failed to reset tours in database:', err)
    }
  }
}

/**
 * Check if user should see tour on page load (synchronous)
 * - Regular users: show if not seen before
 * - Admin users: don't auto-show (they use toolbar toggle)
 */
export function shouldShowTourOnLoad(tour: keyof ToursShown, isAdmin: boolean): boolean {
  // Admins control tours via toolbar
  if (isAdmin) {
    return false
  }

  // Regular users see tour first time only (check cookies synchronously)
  return !hasTourBeenShown(tour)
}
